/**
 * Hulk Form Builder Modal System
 *
 * Features:
 * - Uses Hulk's RenderFormById API for dynamic form loading
 * - Pre-rendered forms: Forms can also be pre-rendered hidden on page load, then moved to modal
 * - Fallback: If form not pre-rendered, uses RenderFormById to load dynamically
 * - Accessible: Keyboard navigation, focus trapping, ARIA attributes
 */
(function() {
  'use strict';

  const HulkFormModal = {
    modal: null,
    container: null,
    preloadContainer: null,
    closeBtn: null,
    currentFormId: null,
    originalParents: {}, // Store original parent to return form on close
    isOpen: false,
    previousActiveElement: null,
    loadedForms: {}, // Track which forms have been loaded dynamically

    init() {
      this.modal = document.getElementById('hulk-form-modal');
      this.container = document.getElementById('hulk-form-container');
      this.preloadContainer = document.getElementById('hulk-forms-preload');

      if (!this.modal || !this.container) {
        console.warn('HulkFormModal: Modal elements not found');
        return;
      }

      this.closeBtn = this.modal.querySelector('.hulk-modal__close');
      this.bindEvents();
    },

    bindEvents() {
      // Handle all clicks for popup triggers
      document.addEventListener('click', (e) => {
        // Check for button with data-hulk-form attribute
        const triggerBtn = e.target.closest('[data-hulk-form]');
        if (triggerBtn) {
          e.preventDefault();
          this.open(triggerBtn.dataset.hulkForm);
          return;
        }

        // Check for links with #hulk: prefix
        const link = e.target.closest('a[href^="#hulk:"]');
        if (link) {
          e.preventDefault();
          const formId = link.getAttribute('href').replace('#hulk:', '');
          this.open(formId);
          return;
        }

        // Close button or overlay
        if (e.target.closest('[data-hulk-close]')) {
          e.preventDefault();
          this.close();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Trap focus within modal
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          this.handleTabKey(e);
        }
      });
    },

    open(formId) {
      if (!formId || this.isOpen) return;

      // Store currently focused element to restore later
      this.previousActiveElement = document.activeElement;
      this.currentFormId = formId;

      // Clear any previous content
      this.container.innerHTML = '';

      // First, try to find a pre-rendered form in the preload container
      let formElement = document.getElementById(formId);

      if (formElement && formElement.classList.contains('pxFormGenerator')) {
        // Check if it has actual content (Hulk rendered it)
        if (formElement.innerHTML.trim().length > 50 || formElement.querySelector('form')) {
          // Store original parent so we can return it on close
          this.originalParents[formId] = formElement.parentNode;

          // Move form to modal
          this.container.appendChild(formElement);
          this.showModal();
          return;
        }
      }

      // Form not pre-rendered - try using Hulk's APIs
      // Show loading state
      this.container.innerHTML = `
        <div class="hulk-modal__loading">
          <div class="hulk-modal__spinner"></div>
          <p>Loading form...</p>
        </div>
      `;
      this.showModal();

      // Create a container for the form with the exact ID Hulk expects
      const formContainer = document.createElement('div');
      formContainer.className = 'pxFormGenerator';
      formContainer.id = formId; // Use the exact form ID

      // Replace loading state with form container
      this.container.innerHTML = '';
      this.container.appendChild(formContainer);

      // Try multiple initialization methods
      let initialized = false;

      // Method 1: RenderFormById (if available)
      if (typeof window.RenderFormById === 'function') {
        try {
          window.RenderFormById(formId, formId);
          initialized = true;
        } catch (e) {
          console.warn('HulkFormModal: RenderFormById failed', e);
        }
      }

      // Method 2: hulkFBInitFormBuilder (global initializer)
      if (!initialized && typeof window.hulkFBInitFormBuilder === 'function') {
        try {
          window.hulkFBInitFormBuilder();
          initialized = true;
        } catch (e) {
          console.warn('HulkFormModal: hulkFBInitFormBuilder failed', e);
        }
      }

      // Method 3: Dispatch custom event for dynamic content
      document.dispatchEvent(new CustomEvent('shopify:section:load'));

      // Method 4: Trigger MutationObserver-based initialization by forcing a small DOM change
      setTimeout(() => {
        formContainer.setAttribute('data-hulk-init', 'true');
      }, 50);

      // Check if form rendered successfully after a delay
      setTimeout(() => {
        if (!formContainer.querySelector('form') && formContainer.innerHTML.trim().length < 50) {
          this.showError(formId, 'Form failed to load. Please ensure Hulk Form Builder app embed is enabled in Theme Settings → App embeds.');
        }
      }, 3000);

      return;
    },

    showError(formId, message) {
      this.container.innerHTML = `
        <div class="hulk-modal__error">
          <p><strong>Unable to load form</strong></p>
          <p style="font-size: 0.875rem; color: #666; margin-top: 0.5rem;">
            ${message}
          </p>
          <p style="font-size: 0.875rem; color: #666; margin-top: 0.5rem;">
            Form ID: <code style="background: #f5f5f5; padding: 2px 6px; border-radius: 3px;">${formId}</code>
          </p>
          <p style="font-size: 0.875rem; color: #666; margin-top: 0.5rem;">
            To pre-load this form, add the Form ID to:<br>
            Theme Settings → Hulk Form Builder Popups → Popup Form IDs
          </p>
          <button type="button" class="button button--secondary" data-hulk-close style="margin-top: 1rem;">Close</button>
        </div>
      `;
    },

    showModal() {
      this.isOpen = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('hulk-modal-open');

      // Focus the close button for accessibility
      if (this.closeBtn) {
        setTimeout(() => this.closeBtn.focus(), 100);
      }
    },

    close() {
      this.isOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('hulk-modal-open');

      // Return pre-rendered form to original location (hidden preload container)
      if (this.currentFormId && this.originalParents[this.currentFormId]) {
        const formElement = document.getElementById(this.currentFormId);
        if (formElement) {
          // Reset form before returning
          const form = formElement.querySelector('form');
          if (form) {
            form.reset();
          }

          // Move back to preload container
          this.originalParents[this.currentFormId].appendChild(formElement);
        }
      }

      // For dynamically loaded forms, just reset them
      const dynamicForm = this.container.querySelector('.hulk-dynamic-form form');
      if (dynamicForm) {
        dynamicForm.reset();
      }

      this.currentFormId = null;

      // Restore focus to previous element
      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
    },

    handleTabKey(e) {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    },

    // Public method to open a form programmatically
    openForm(formId) {
      this.open(formId);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => HulkFormModal.init());
  } else {
    HulkFormModal.init();
  }

  // Expose globally for manual triggering
  window.HulkFormModal = HulkFormModal;
})();
